import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import type { Indexed } from '../../../../lib/types';
import { useT } from '../../../../i18n/I18nProvider';
import { canTest, type ModelPrice } from '../aiModels';
import { ModelNameCell, ModelSwitch, PriceCell, RunsOnCell, TestOutcome, UsageCell } from './cells';

export interface AiModelRowsProps {
  models: admin.AdminAiModel[];
  priceOf: (m: admin.AdminAiModel) => ModelPrice;
  ratesFailed: boolean;
  tests: Record<string, admin.AiModelTest>;
  busy: string | null;
  onToggle: (m: admin.AdminAiModel, on: boolean) => void;
  onTest: (m: admin.AdminAiModel) => void;
  onEdit: (m: admin.AdminAiModel) => void;
}

/**
 * One kind's models as a table. Every kind's table carries the same columns at the same widths, so the groups
 * line up down the page and read as one list.
 */
export function AiModelsTable({ models, priceOf, ratesFailed, tests, busy, onToggle, onTest, onEdit }: AiModelRowsProps) {
  const t = useT();
  return (
    <DataTable<Indexed<admin.AdminAiModel>>
      records={models as Indexed<admin.AdminAiModel>[]}
      idAccessor="code"
      emptyState={t('billing.aiModels.empty')}
      columns={[
        { accessor: 'name', title: t('billing.aiModels.columns.model'), width: '20%', render: (m) => <ModelNameCell model={m} /> },
        { accessor: 'provider', title: t('billing.aiModels.columns.provider'), width: '9%', render: (m) => <Text role="bodySmall">{m.provider}</Text> },
        { accessor: 'model_id', title: t('billing.aiModels.columns.runsOn'), width: '19%', render: (m) => <RunsOnCell model={m} /> },
        { accessor: 'price', title: t('billing.aiModels.columns.price'), width: '20%', render: (m) => <PriceCell price={priceOf(m)} ratesFailed={ratesFailed} /> },
        { accessor: 'calls_last30_days', title: t('billing.aiModels.columns.usage'), width: '11%', render: (m) => <UsageCell model={m} /> },
        { accessor: 'enabled', title: t('billing.aiModels.columns.offered'), width: '9%', render: (m) => <ModelSwitch model={m} busy={busy === m.code} onToggle={onToggle} /> },
        {
          accessor: 'actions',
          title: t('common.actions'),
          textAlign: 'right',
          render: (m) => (
            <Stack gap={2} align="flex-end">
              <Group gap="xs" wrap="nowrap" justify="flex-end">
                {canTest(m) && (
                  <Button size="xs" variant="outline" onClick={() => onTest(m)} aria-label={t('billing.aiModels.test.actionFor', { name: m.name })}>
                    {t('action.test')}
                  </Button>
                )}
                <Button size="xs" variant="subtle" onClick={() => onEdit(m)} aria-label={t('billing.aiModels.edit.actionFor', { name: m.name })}>
                  {t('action.edit')}
                </Button>
              </Group>
              <TestOutcome result={tests[m.code]} />
            </Stack>
          )
        }
      ]}
    />
  );
}
